/**
 * Bridges Shopify standard cart events into Bespoke's ThemeEvents cart UI.
 *
 * Apps and agents call `Shopify.actions.updateCart` / `openCart`. The override
 * emits those events from `document`. This module waits for the action promise,
 * re-renders each `cart-items-component`, then dispatches `CartUpdateEvent` with
 * the **total** item count and `source: 'standard-actions'` so the cart icon
 * **sets** the bubble instead of incrementing it.
 */
import { sectionRenderer } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';

const CART_EVENT_NAMES = [
  'shopify:cart:lines-update',
  'shopify:cart:discount-update',
  'shopify:cart:note-update',
];

/**
 * @typedef {Event & {
 *   promise?: Promise<{
 *     cart?: { totalQuantity?: number } | null,
 *     userErrors?: unknown[],
 *   }>
 * }} StandardCartEvent
 */

/**
 * @param {Event} event
 * @returns {boolean}
 */
function isInsideCartItems(event) {
  const target = event.target;
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest('cart-items-component'));
}

/**
 * @param {StandardCartEvent} event
 */
async function handleStandardCartEvent(event) {
  if (isInsideCartItems(event)) return;

  let itemCount = 0;
  /** @type {{ item_count: number } | null} */
  let resource = null;

  if (event.promise) {
    try {
      const result = await event.promise;

      if (result?.userErrors?.length) return;

      itemCount = result?.cart?.totalQuantity ?? 0;
      resource = { item_count: itemCount };
    } catch {
      return;
    }
  }

  const cartItems = document.querySelectorAll('cart-items-component[data-section-id]');
  const renders = [];

  for (const cartItem of cartItems) {
    const sectionId = cartItem.getAttribute('data-section-id');
    if (!sectionId) continue;
    renders.push(sectionRenderer.renderSection(sectionId, { cache: false }));
  }

  await Promise.all(renders);

  document.dispatchEvent(
    new CartUpdateEvent(resource, 'standard-actions', {
      source: 'standard-actions',
      itemCount,
    })
  );
}

for (const eventName of CART_EVENT_NAMES) {
  document.addEventListener(eventName, (event) => {
    void handleStandardCartEvent(/** @type {StandardCartEvent} */ (event));
  });
}
